import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { confirmCreditCharge } from "../api/creditsApi";
import Logger from "../utils/logger";
import "./InteractiveViewer.css"; // 导入CSS文件

const InteractiveViewer = ({ htmlCode, srcUrl, isVisible, onClose, onRenderError, isThumbnail = false, isEmbedded = false, allowDownload = true, chargeSession = null, onFinalResultDelivered = null }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fetchedHtml, setFetchedHtml] = useState(null);
  const iframeRef = useRef(null);
  const popupWindowRef = useRef(null);
  const iframeErrorRef = useRef(false);
  const confirmedChargeSessionRef = useRef(null);
  const deliveredResultRef = useRef(null);

  const confirmFinalHtmlDelivered = async () => {
    const deliveryKey = String(htmlCode || srcUrl || "").slice(0, 200);
    if (deliveryKey && deliveredResultRef.current === deliveryKey) return;

    const sessionId = chargeSession?.sessionId;
    let confirmResult = null;
    if (sessionId && confirmedChargeSessionRef.current !== sessionId) {
      confirmedChargeSessionRef.current = sessionId;
      confirmResult = await confirmCreditCharge({
        sessionId,
        resultType: "html",
        resultRef: deliveryKey,
      });
      if (!confirmResult.success) {
        confirmedChargeSessionRef.current = null;
        if (onRenderError) {
          onRenderError(confirmResult.message || "确认扣费失败，请重试");
        }
        return;
      }
    }

    if (deliveryKey) {
      deliveredResultRef.current = deliveryKey;
    }
    if (onFinalResultDelivered) {
      onFinalResultDelivered({ htmlCode, srcUrl }, confirmResult);
    }
  };

  // chargeSession 可能晚于内容加载完成到达；补一次确认，避免错过扣费触发。
  useEffect(() => {
    if ((!htmlCode && !srcUrl) || !chargeSession?.sessionId) return;
    confirmFinalHtmlDelivered();
  }, [chargeSession?.sessionId, htmlCode, srcUrl]);

  // 通过postMessage监听来自iframe的错误
  useEffect(() => {
    const handleMessage = (event) => {
      // 为安全起见，可以检查event.origin
      // if (event.origin !== 'expected-origin') return;

      if (event.data && event.data.type === "iframe-error") {
        iframeErrorRef.current = true;
        const errorMessage =
          event.data.payload.message || "Iframe中发生未知渲染错误";
        Logger.error(
          "Iframe渲染时发生脚本错误 (via postMessage):",
          event.data.payload
        );
        if (onRenderError) {
          onRenderError(errorMessage);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onRenderError]);

  // 清理弹窗
  const cleanup = () => {
    if (popupWindowRef.current && !popupWindowRef.current.closed) {
      popupWindowRef.current.close();
      popupWindowRef.current = null;
    }
  };

  // 组件卸载时清理
  useEffect(() => {
    return cleanup;
  }, []);

  // 将HTML写入iframe的辅助函数
  const writeHtmlToIframe = (html) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    iframeErrorRef.current = false;

    // 设置一个加载超时
    const loadTimeout = setTimeout(() => {
      Logger.warn("Iframe加载超时，可能存在问题");
      if (onRenderError) {
        onRenderError("渲染超时，可能代码中存在无限循环或严重性能问题。");
      }
    }, 50000); // 50秒超时

    // 监听iframe加载完成事件
    iframe.onload = () => {
      clearTimeout(loadTimeout);
      Logger.info("Iframe内容已加载");

      // 检查渲染结果
      try {
        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow.document;
        // 添加一个延迟检查，因为脚本可能在onload后才完全执行完毕
        setTimeout(() => {
          if (!iframeDoc.body || iframeDoc.body.innerHTML.trim() === "") {
            Logger.warn("Iframe body为空，可能渲染失败");
            if (onRenderError) {
              onRenderError("渲染未生成任何可见内容，请检查代码。");
            }
            return;
          }

          if (!iframeErrorRef.current) {
            confirmFinalHtmlDelivered();
          }
        }, 500);
      } catch (e) {
        // 跨域访问可能导致异常，但 iframe 已成功触发 onload，视为内容已交付
        Logger.warn("无法检查iframe内容（跨域限制），但 iframe 已加载，确认扣费");
        confirmFinalHtmlDelivered();
      }
    };

    // 注入错误捕获脚本和响应式样式到iframe
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

    // 增强的响应式样式，完全适配各种分辨率和缩放比例
    const responsiveStyles = `
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes">
      <style>
        /* 基础重置 - 确保一致的盒模型 */
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        /* 根元素使用相对单位，适配不同DPI */
        html {
          font-size: 16px;
          -webkit-text-size-adjust: 100%;
          -moz-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }
        
        /* body基础样式 - 使用flex确保内容居中和适配 */
        html, body {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          overflow-y: auto;
          position: relative;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
          line-height: 1.6;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        /* 所有块级元素使用相对单位 */
        div, section, article, main, header, footer, nav {
          max-width: 100%;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        /* 图片和媒体内容完全响应式 */
        img, video, canvas, svg {
          max-width: 100% !important;
          height: auto !important;
          display: block;
        }
        
        /* 嵌套iframe响应式 */
        iframe {
          max-width: 100%;
          border: none;
        }
        
        /* 表格响应式 */
        table {
          width: 100%;
          max-width: 100%;
          border-collapse: collapse;
          display: block;
          overflow-x: auto;
        }
        
        /* 文本元素使用相对单位 */
        h1 { font-size: 2rem; margin: 1rem 0; }
        h2 { font-size: 1.75rem; margin: 0.875rem 0; }
        h3 { font-size: 1.5rem; margin: 0.75rem 0; }
        h4 { font-size: 1.25rem; margin: 0.625rem 0; }
        h5 { font-size: 1.125rem; margin: 0.5rem 0; }
        h6 { font-size: 1rem; margin: 0.5rem 0; }
        p { font-size: 1rem; margin: 0.5rem 0; }
        
        /* 列表样式 */
        ul, ol {
          padding-left: 2rem;
          margin: 0.5rem 0;
        }
        
        li {
          margin: 0.25rem 0;
        }
        
        /* 强制长单词和URL换行 */
        * {
          word-break: break-word;
          hyphens: auto;
        }
        
        /* 容器元素添加内边距，防止内容贴边 */
        body > * {
          padding-left: max(1rem, env(safe-area-inset-left));
          padding-right: max(1rem, env(safe-area-inset-right));
        }
        
        /* 防止固定定位元素超出视口 */
        [style*="position: fixed"],
        [style*="position:fixed"] {
          max-width: 100vw !important;
        }
        
        /* 防止绝对定位元素导致布局问题 */
        [style*="position: absolute"],
        [style*="position:absolute"] {
          max-width: 100% !important;
        }
        
        /* 覆盖可能存在的固定宽度 */
        [style*="width"] {
          max-width: 100% !important;
        }
        
        /* 响应式媒体查询 - 小屏幕优化 */
        @media (max-width: 768px) {
          html {
            font-size: 14px;
          }
          
          body > * {
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
          
          h1 { font-size: 1.75rem; }
          h2 { font-size: 1.5rem; }
          h3 { font-size: 1.25rem; }
        }
        
        @media (max-width: 480px) {
          html {
            font-size: 13px;
          }
          
          body > * {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          h1 { font-size: 1.5rem; }
          h2 { font-size: 1.25rem; }
          h3 { font-size: 1.125rem; }
        }
        
        /* 高DPI屏幕优化 */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          html {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        }
        
        /* 确保内容在缩放时保持可读性 */
        @media (min-width: 1920px) {
          body {
            max-width: 1800px;
            margin: 0 auto;
          }
        }
      </style>
    `;

    const errorHandlingScript = `
      <script>
        window.onerror = function(message, source, lineno, colno, error) {
          window.parent.postMessage({ 
            type: 'iframe-error', 
            payload: { 
              message: \`\${message} at \${source.split('/').pop()}:\${lineno}:\${colno}\`
            }
          }, '*');
          return true;
        };
        window.addEventListener('unhandledrejection', function(event) {
          const reason = event.reason || {};
          window.parent.postMessage({ 
            type: 'iframe-error', 
            payload: { 
              message: '未处理的Promise拒绝: ' + (reason.message || event.reason) 
            }
          }, '*');
        });
      <\/script>
    `;

    iframeDoc.open();
    iframeDoc.write(responsiveStyles + errorHandlingScript + html);
    iframeDoc.close();

    Logger.info("HTML代码已写入iframe (with injected error handler)");
  };

  // 在iframe中显示HTML
  const displayInIframe = async () => {
    if (iframeRef.current) {
      // 如果有直接的HTML代码，优先使用
      if (htmlCode) {
        writeHtmlToIframe(htmlCode);
        return;
      }

      // 如果有URL，尝试加载
      if (srcUrl) {
        // 尝试通过fetch获取内容，以解决Content-Disposition导致下载的问题
        try {
          // 简单的fetch尝试
          const response = await fetch(srcUrl, { method: 'GET' });
          if (response.ok) {
            const text = await response.text();
            // 检查内容是否看起来像HTML
            if (text && (text.trim().startsWith('<') || text.includes('<html') || text.includes('<body'))) {
              Logger.info("成功通过fetch获取HTML内容");
              setFetchedHtml(text);
              writeHtmlToIframe(text);
              return;
            }
          }
        } catch (error) {
          Logger.warn("Fetch HTML failed, falling back to src:", error);
        }

        // 如果fetch失败或内容不对，回退到直接设置src
        // 这对于已有Content-Disposition: attachment的文件会导致下载
        iframeRef.current.src = srcUrl;
        Logger.info("Using srcUrl for iframe:", srcUrl);
      }
    }
  };

  // 在新窗口中打开
  const openInNewWindow = () => {
    const content = htmlCode || fetchedHtml;
    
    if (!content && !srcUrl) {
      Logger.warn("没有HTML代码或URL可显示");
      return;
    }

    try {
      // 关闭之前的弹窗
      cleanup();

      // 创建新窗口
      const popup = window.open(
        srcUrl || "",
        "_blank",
        "width=1200,height=800,scrollbars=yes,resizable=yes"
      );

      if (popup) {
        if (content && !srcUrl) {
             popup.document.open();
             popup.document.write(content);
             popup.document.close();
        } else if (content && srcUrl) {
             // 即使有srcUrl，如果我们已经fetch了内容，也可以写入，这样可以保留我们的样式注入
             // 但通常新窗口打开最好用原始URL，除非原始URL会强制下载
             // 这里我们做一个折中：如果有srcUrl，优先用srcUrl让浏览器处理。
             // 如果srcUrl会导致下载（我们无法预知，但在新窗口下载也算一种行为），
             // 如果我们想强制预览，可以写入content。
             // 但为了保持原汁原味，新窗口通常倾向于srcUrl。
        }

        popup.document.title = "交互式数学演示 - Hammerath";

        popupWindowRef.current = popup;

        // 监听弹窗关闭事件
        const checkClosed = () => {
          if (popup.closed) {
            popupWindowRef.current = null;
            Logger.info("交互式窗口已关闭");
          } else {
            setTimeout(checkClosed, 500);
          }
        };
        setTimeout(checkClosed, 500);

        Logger.info("交互式HTML已在新窗口中打开");
      } else {
        Logger.error("无法打开新窗口，可能被浏览器阻止");
        alert("无法打开新窗口，请检查浏览器弹窗拦截设置");
      }
    } catch (error) {
      Logger.error("打开新窗口失败", error);
      alert("打开新窗口失败: " + error.message);
    }
  };

  // 全屏切换
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 下载HTML文件
  const downloadHTML = () => {
    const content = htmlCode || fetchedHtml;
    
    if (!content) {
      // 如果没有内容但有URL，尝试直接下载URL
      if (srcUrl) {
        const a = document.createElement("a");
        a.href = srcUrl;
        a.download = ""; // Let browser decide name
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }
      
      Logger.warn("没有HTML代码可下载");
      return;
    }

    try {
      const blob = new Blob([content], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `交互式数学演示_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      Logger.info("HTML文件下载已启动");
    } catch (error) {
      Logger.error("下载HTML文件失败", error);
      alert("下载失败: " + error.message);
    }
  };

  // 仅当查看器变为可见或全屏状态改变时才渲染iframe
  useEffect(() => {
    if (isVisible && (htmlCode || srcUrl)) {
      // 在重新加载前清除之前的错误
      if (onRenderError) {
        onRenderError(null); // 传递null或''来清除错误
      }
      setTimeout(displayInIframe, 100); // 稍微延迟以确保iframe准备就绪
    }
  }, [isVisible, isFullscreen]); // 添加 isFullscreen 依赖，因为Portal会导致iframe重置

  if (!isVisible) {
    return null;
  }

  const viewerStyle = isThumbnail ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    borderRadius: 0,
    padding: 0,
    display: 'block' // 覆盖flex布局，避免子元素高度计算问题
  } : isEmbedded ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  } : {
    // 弹窗模式强制置顶，避免被灵动岛或其他固定浮层遮挡
    zIndex: 2147483000
  };

  const ViewerContent = (
    <div className={`threejs-viewer ${isFullscreen ? "fullscreen" : ""} ${isThumbnail ? "thumbnail-mode" : ""} ${isEmbedded ? "embedded-mode" : ""}`} style={viewerStyle}>
      <div className="viewer-header" style={isThumbnail ? { display: 'none' } : {}}>
        <h3>
          <i className="ri-computer-line"></i>
          交互式演示
        </h3>
        <div className="viewer-controls">
          <button
            className="control-btn"
            onClick={openInNewWindow}
            title="在新窗口中打开"
          >
            <i className="ri-external-link-line"></i>
            新窗口打开
          </button>
          {allowDownload && (
            <button
              className="control-btn"
              onClick={downloadHTML}
              title="下载HTML文件"
            >
              <i className="ri-download-line"></i>
              下载
            </button>
          )}
          {/* 只有在非嵌入模式（弹窗模式）下才显示关闭按钮 */}
          {(!isEmbedded || isFullscreen) && (
            <button
              className="control-btn close-btn"
              onClick={onClose}
              title="关闭"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>
      </div>

      <div className="viewer-content" style={isThumbnail ? { 
        height: '400%', 
        width: '400%',
        transform: 'scale(0.25)',
        transformOrigin: 'top left',
        padding: 0,
        pointerEvents: 'none',
        position: 'absolute', // 绝对定位确保尺寸计算不受flex影响
        top: 0,
        left: 0
      } : isEmbedded ? {
        flex: 1,
        width: '100%',
        height: '100%',
        padding: 0
      } : {}}>
        {htmlCode || srcUrl ? (
          <iframe
            ref={iframeRef}
            className="html-iframe"
            title="交互式演示"
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        ) : (
          <div className="no-content">
            <i className="ri-code-line"></i>
            <p>暂无内容</p>
          </div>
        )}
      </div>

      <div className="viewer-info" style={isThumbnail ? { display: 'none' } : {}}>
        <p>
          <i className="ri-information-line"></i>
          提示：你可以在iframe中交互，或点击"新窗口打开"获得更好的体验
        </p>
      </div>
    </div>
  );

  if (!isEmbedded && !isThumbnail) {
    return ReactDOM.createPortal(ViewerContent, document.body);
  }

  return ViewerContent;
};

export default InteractiveViewer;
